import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { TabContent, TabPane } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import { Loading } from '@whitelabel/component-library';
import { PDSPolicyContext } from '@whitelabel/component-library/src/components/TransformLink/helpers';
import { STATUS_CONFIRMED } from '@whitelabel/helpers/constants';
import { IUser } from '@whitelabel/helpers/types';
import {
  ACCOUNT_NAV_ITEMS,
  PDS_LOGIN_MODAL_DISPLAYED,
  XCOVER_PARTNER_ID,
} from '@whitelabel/xcover-shared/helpers/constants';
import { IError, IQuotePackageQuote } from '@whitelabel/xcover-shared/helpers/types';
import { storeCustomerRegionFromURL } from '@whitelabel/xcover-shared/helpers/multiRegion';
import { PartnerIDContext } from '../../../helpers/utils';
import ErrorPage from '../../../components/ErrorPage';
import { StyledLayout } from '../../../styled/StyledLayout';
import NavBar from '../../layout/NavBar';
import PageMeta from '../../helpers/PageMeta';
import { PDSHeader, PDSPolicyContent } from '../../../components/PDS';
import { getModifiedBenefits } from '../../../helpers/cover';
import LoginModal from '../../../components/LoginModal';
import { useGetPDSQuery } from '../../../store/services/xcover/endpoints/pds';
import messages from './messages';
import getActivePolicy from './helper';
import { StyledContainer } from './styledPDS';

interface IPDSProps {
  user?: IUser;
  id: string;
}

const PDS = ({ user, id }: IPDSProps) => {
  const intl = useIntl();
  const location = useLocation();

  const {
    data: pds,
    isFetching,
    error: fetchError,
  } = useGetPDSQuery({ id, locale: intl.locale, auth: !!user, search: location.search });

  const error = (fetchError as any)?.data as IError;

  const navItems = user && !isFetching ? ACCOUNT_NAV_ITEMS : [];
  const [loginModalIsOpen, setLoginModalIsOpen] = useState(false);
  const isPartnerPhoneBased = pds?.partner.signupMethodOnXcover === 'phone';

  const toggleModalOpen = () => {
    setLoginModalIsOpen((prev) => !prev);
  };

  useEffect(() => {
    storeCustomerRegionFromURL();
  }, [storeCustomerRegionFromURL]);

  useEffect(() => {
    const pdsLoginModalDisplayed = sessionStorage.getItem(PDS_LOGIN_MODAL_DISPLAYED);
    if (!user && !pdsLoginModalDisplayed && pds?.status === STATUS_CONFIRMED) {
      setLoginModalIsOpen(true);
    }
  }, [user, pds]);

  const partner = pds ? pds.partner.subsidiary : null;
  const partnerTransactionId = pds ? pds.partnerTransactionId : '';

  const policies = useMemo(() => {
    let policiesArr;
    if (pds && pds.policyVersions && pds.policyVersions.length) {
      if (pds.id) {
        policiesArr = pds.policyVersions.map(({ policy, pdsContent: { body }, quotes }: any) => {
          const { policyCode, policyType } = policy;
          const { benefits, insured, benefitsLabelConfig } = quotes[0];
          const attributesArray = quotes.map((quote: IQuotePackageQuote) => quote.attributes);
          return {
            ...policy,
            body,
            benefits: getModifiedBenefits(policyType, policyCode, intl, benefits),
            insured,
            attributesArray,
            benefitsLabelConfig,
          };
        });
      } else {
        policiesArr = pds.policyVersions.map(({ policy, benefits, pdsContent: { body } }: any) => ({
          ...policy,
          body,
          benefits,
        }));
      }
    }
    return policiesArr;
  }, [pds]);

  const [activePolicy, setActivePolicy] = useState(policies ? policies[0].policyVersion : null);

  useEffect(() => {
    if (policies) {
      setActivePolicy(getActivePolicy(policies, location));
    }
  }, [setActivePolicy, location, policies]);

  if (error) {
    return <ErrorPage error={error} button={{ link: '/account' }} />;
  }

  return (
    <PartnerIDContext.Provider value={{ partnerID: partner?.id }}>
      <StyledLayout
        navBar={
          <NavBar
            items={navItems}
            cobrand={!user && partner && partner.id !== XCOVER_PARTNER_ID ? partner : undefined}
            background
          />
        }
        footerType="slim"
        gray
      >
        <PageMeta title={intl.formatMessage(messages.seoTitle)} />

        {isFetching ? (
          <Loading />
        ) : (
          pds && (
            <StyledContainer>
              <PDSHeader
                user={user}
                partner={partner}
                policies={policies}
                activePolicy={activePolicy}
                setActivePolicy={setActivePolicy}
                className="hide-on-print"
              />
              <TabContent activeTab={activePolicy}>
                {policies.map((policy: any, idx: number) => (
                  <PDSPolicyContext.Provider
                    value={{
                      appendPDSUTMSearchParams: true,
                      policyType: policy.policyType,
                      policyVersion: policy.policyVersion,
                    }}
                    key={policy.policyVersion}
                  >
                    <PDSHeader
                      user={user}
                      partner={partner}
                      policies={[policies[idx]]}
                      activePolicy={activePolicy}
                      setActivePolicy={setActivePolicy}
                      className="show-on-print-only"
                    />
                    <TabPane key={policy.policyVersion} tabId={policy.policyVersion}>
                      <PDSPolicyContent
                        currency={pds.currency}
                        user={user}
                        policy={policy}
                        activePolicy={activePolicy}
                        partnerTransactionId={partnerTransactionId}
                        partnerId={partner.id}
                      />
                    </TabPane>
                  </PDSPolicyContext.Provider>
                ))}
              </TabContent>
              <LoginModal
                toggleModalOpen={toggleModalOpen}
                isOpen={loginModalIsOpen}
                loginModalDisplayPlace={PDS_LOGIN_MODAL_DISPLAYED}
                isPartnerPhoneBased={isPartnerPhoneBased}
              />
            </StyledContainer>
          )
        )}
      </StyledLayout>
    </PartnerIDContext.Provider>
  );
};

const mapStateToProps = (state: any, ownProps: any) => {
  const { id } = ownProps.match.params;
  const { data: user } = state.user;

  return {
    user,
    id,
  };
};

export default connect(mapStateToProps)(PDS);
